import React, { useEffect, useState, lazy, Suspense } from "react";
import "./App.css";
import Navbar from "./components/Navbar";
import Header from "./components/Header";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";

import {
  BrowserRouter as Router ,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

import ProtectedRoute from "./components/ProtectedRoute";
import { isAuthenticated, getUser } from "./auth";

// ===== USER PAGES =====
import ProductManagementBody from "./pages/ProductManagementBody";
import ProductHistory from "./pages/ProductHistory";
import UserManagement from "./pages/UserManagement";
import AddNewProduct from "./pages/AddNewProduct";
import PriceManagement from "./pages/PriceManagement";
import MarginCalculator from "./pages/MarginCalculator";
import CalculateMargin from "./pages/CalculateMargin";
import MarginDetails from "./pages/MarginDetails";
import AddNewQuote from "./pages/AdNewQuotes";
import QuoteDetails from "./pages/QuoteDetails";
import Quotes from "./pages/Quotes";

// ===== CUSTOMER PORTAL =====
import CustomerLayout from './portals/customer/components/CustomerLayout.jsx';
import CustomerDashboard from './portals/customer/pages/Dashboard.jsx';
import CustomerMyOrders from './portals/customer/pages/MyOrders.jsx';
import CustomerPriceList from './portals/customer/pages/PriceList.jsx';
import CustomerNewOrder from './portals/customer/pages/NewOrder.jsx';
import CustomerOrderView from './portals/customer/pages/OrderView.jsx';

// ===== AUTH PAGES =====
import LoginPage from "./pages/LoginPage";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPasswordPage";
import Dashboard from "./pages/Dashboard";
import SendResetLinkPage from "./pages/SendResetLinkPage";
import UpdatePasswordPage from "./pages/UpdatePasswordPage";
import SessionTimeout from "./pages/SessionTimeout";

const Orders = lazy(() => import("./pages/Orders"));
const AddCustomerOrder = lazy(() => import("./pages/AddNewCustomerOrders"));
const OrderDetails = lazy(() => import("./pages/OrderDetails"));

function RouteLoader() {
  return (
    <div style={{ minHeight: "50vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 34, color: "#f50087" }} spin />} />
        <span style={{ color: "#f50087", fontWeight: 600 }}>Loading</span>
      </div>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = getUser();

    if (storedUser) {
      setUser(storedUser);
    } else {
      setUser(null);
    }

    setLoading(false);
  }, []);

  if (loading) return null;

  const loggedIn = isAuthenticated();
  const isVocovoUser = user?.email?.toLowerCase()?.includes("vocovo");

  return (
    <Router basename="/">
      <Routes>
        {/* PUBLIC AUTH ROUTES */}
        <Route
  path="/login"
  element={<LoginPage />}
/>


        <Route path="/auth/forgot-password" element={<ForgotPassword />} />
        <Route path="/send-reset-link" element={<SendResetLinkPage />} />
        <Route path="/auth/reset-password" element={<ResetPassword />} />
        <Route path="/auth/update-password" element={<UpdatePasswordPage />} />

        {/* SESSION TIMEOUT (MUST BE PUBLIC) */}
        <Route path="/session-timeout" element={<SessionTimeout />} />

        {/* ================= INTERNAL DASHBOARD ================= */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute page="dashboard">
              <Layout>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* ================= INTERNAL USER ROUTES ================= */}
        <Route
          path="/users"
          element={
            <ProtectedRoute page="users">
              <Layout>
                <UserManagement />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/products"
          element={
            <ProtectedRoute page="products">
              <Layout>
                <ProductManagementBody />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/products/:id/history"
          element={
            <ProtectedRoute page="products">
              <Layout>
                <ProductHistory />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/products/add-product"
          element={
            <ProtectedRoute page="products">
              <Layout>
                <AddNewProduct />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/products/edit/:productId"
          element={
            <ProtectedRoute page="products">
              <Layout>
                <AddNewProduct />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/discounts"
          element={
            <ProtectedRoute page="discounts">
              <Layout>
                <PriceManagement />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/margin-calculator"
          element={
            <ProtectedRoute page="marginCalculator">
              <Layout>
                <MarginCalculator />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/calculate-margin"
          element={
            <ProtectedRoute page="marginCalculator">
              <Layout>
                <CalculateMargin />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/margin-calculator/:id"
          element={
            <ProtectedRoute page="marginCalculator">
              <Layout>
                <MarginDetails />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/orders"
          element={
            <ProtectedRoute page="orders">
              <Layout>
                <Suspense fallback={<RouteLoader />}>
                  <Orders />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          }
        />
<Route
          path="/orders/add-order"
          element={
            <ProtectedRoute page="orders">
              <Layout>
                <Suspense fallback={<RouteLoader />}>
                  <AddCustomerOrder />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/orders/edit/:orderId"
          element={
            <ProtectedRoute page="orders">
              <Layout>
                <Suspense fallback={<RouteLoader />}>
                  <AddCustomerOrder />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          }
        />
         <Route
          path="/orders/view/:orderId"
          element={
            <ProtectedRoute page="orders">
              <Layout>
                <Suspense fallback={<RouteLoader />}>
                  <OrderDetails />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/quotes"
          element={
            <ProtectedRoute page="orders">
              <Layout>
                <Quotes />
              </Layout>
            </ProtectedRoute>
          }
        />
<Route
          path="/quotes/add-quote"
          element={
            <ProtectedRoute page="orders">
              <Layout>
                <AddNewQuote />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/quotes/edit/:quoteId"
          element={
            <ProtectedRoute page="orders">
              <Layout>
                <AddNewQuote />
              </Layout>
            </ProtectedRoute>
          }
        />
         <Route
          path="/quotes/view/:quoteId"
          element={
            <ProtectedRoute page="orders">
              <Layout>
                <QuoteDetails />
              </Layout>
            </ProtectedRoute>
          }
        />
        
        {/* ================= CUSTOMER PORTAL ROUTES ================= */}
        <Route path="/customer" element={<CustomerLayout />}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route
            path="dashboard"
            element={
              <ProtectedRoute page="dashboard">
                <CustomerDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="orders"
            element={
              <ProtectedRoute page="orders">
                <CustomerMyOrders />
              </ProtectedRoute>
            }
          />
          <Route
            path="orders/new"
            element={
              <ProtectedRoute page="orders">
                <CustomerNewOrder />
              </ProtectedRoute>
            }
          />
          <Route
            path="orders/edit/:orderId"
            element={
              <ProtectedRoute page="orders">
                <CustomerNewOrder />
              </ProtectedRoute>
            }
          />
          <Route
            path="orders/:orderId"
            element={
              <ProtectedRoute page="orders">
                <CustomerOrderView />
              </ProtectedRoute>
            }
          />
          <Route
            path="price-listing"
            element={
              <ProtectedRoute page="pricelist">
                <CustomerPriceList />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* ================= DEFAULT REDIRECTS ================= */}
        <Route
          path="/"
          element={
            loggedIn ? (
              <Navigate
                to={isVocovoUser ? "/dashboard" : "/customer/dashboard"}
                replace
              />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        {/* MUST BE LAST */}
<Route
  path="*"
  element={
    window.location.pathname === "/session-timeout" ? (
      <SessionTimeout />
    ) : isAuthenticated() ? (
      <Navigate
        to={
          getUser()?.userType === "customer"
            ? "/customer/dashboard"
            : "/dashboard"
        }
        replace
      />
    ) : (
      <Navigate to="/login" replace />
    )
  }
/>

      </Routes>
    </Router>
  );
}

function Layout({ children }) {
  return (
    <div className="App" style={{ display: "flex", flexDirection: "row" }}>
      {/* <Navbar /> */}
      <div className="App-main">
        <div className="App-container">
          <Header />
          {children}
        </div>
      </div>
    </div>
  );
}

export default App;
